import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { UserEndpointQueryKeys } from '../../Shared/constants/user/endpoints';
import { UserRoles, isAdminRole } from '../../Shared/constants/userRoles';
import { ApiError } from '../../Shared/interfaces/ApiError';
import { API_STATUS_CODES } from '../../Shared/constants/apiStatusCodes';
import { UserInterface } from '../../Shared/interfaces/user/UserInterfaces';
import useApiClient from '../../Shared/services/api';
import { createUser } from '../../Shared/services/user/userService';
import { CreateUserProps } from '../interfaces/userInterfaces';
import { validateUserFields } from '../utils/fieldValidation';
import { getInputFieldsConfig } from '../utils/userInputFieldConfigs';
import { useGetCategories } from './useGetCategories';
import { useEditUserMutation } from '../components/UserManagement/hooks/useEditUserMutation';

export const useCreateUser = (props: CreateUserProps) => {
    const { onClose, customSnackbarMessage, setCustomSnackbarMessage } = props;
    const { t } = useTranslation();
    const [userRole, setUserRole] = useState<string>('');

    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [emailId, setEmailId] = useState<string>('');
    const [privilegedMode, setPrivilegedMode] = useState<boolean>(false);
    const [category, setCategory] = useState<string | null>(null);
    const [orgName, setOrgName] = useState<string | null>(null);

    // Deleted user restore state
    const [deletedUserRestorePopover, setDeletedUserRestorePopover] = useState<boolean>(false);

    const [fieldErrors, setFieldErrors] = useState<{
        firstName: string;
        lastName: string;
        emailId: string;
        userRole: string;
        category: string;
        orgName: string;
    }>({
        firstName: '',
        lastName: '',
        emailId: '',
        userRole: '',
        category: '',
        orgName: '',
    });

    const isUserRoleAccountExecutive = userRole === UserRoles.ACCOUNT_EXECUTIVE;
    const queryClient = useQueryClient();
    const api = useApiClient();
    const {
        data: userCategories,
        isLoading: categoriesLoading,
        error: categoriesError,
    } = useGetCategories(isUserRoleAccountExecutive);

    const inputFields = getInputFieldsConfig(
        firstName,
        lastName,
        setFirstName,
        setLastName,
        t,
        emailId,
        setEmailId
    );

    const createUserMutation = useMutation<any, ApiError, Partial<UserInterface>>(
        {
            mutationFn: (newUserData: Partial<UserInterface>) =>
                createUser(api, newUserData),
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [UserEndpointQueryKeys.GET_USERS],
                });
                setCustomSnackbarMessage(t('user-created-success-message'));
                onClose();
            },
        }
    );

    // Edit user mutation for restoring deleted users
    const editUserMutation = useEditUserMutation(
        setDeletedUserRestorePopover,
        setCustomSnackbarMessage
    );

    // Detect 409 conflict response (user already exists but deleted)
    useEffect(() => {
        if (createUserMutation.isError && createUserMutation.error.statusCode === API_STATUS_CODES.CONFLICT_RESPONSE_CODE) {
            setDeletedUserRestorePopover(true);
        }
    }, [createUserMutation.isError, createUserMutation.error]);

    // Clean-up: Close popover if component unmounts
    useEffect(() => {
        return () => {
            setDeletedUserRestorePopover(false);
        };
    }, []);

    // Handle restoring deleted user
    const handleRestoreDeletedUser = () => {
        const userData: Partial<UserInterface> = {
            userId: emailId.split('@')[0],
            firstName: firstName,
            lastName: lastName,
            emailId: emailId,
            privilegedMode: isAdminRole(userRole) ? true : privilegedMode,
            userRole: userRole,
            category: category,
            orgName: orgName,
        };
        editUserMutation.mutate({
            userId: userData.userId as string,
            userData: userData,
            successMessage: t('user-restored-success-message'),
        });
        setDeletedUserRestorePopover(false);
        onCancel();
    };

    const onCancel = () => {
        createUserMutation.reset();
        onClose();
    };

    const handleSubmit = () => {
        const errors = validateUserFields(
            firstName,
            lastName,
            t,
            emailId,
            userRole,
            category,
            orgName
        );
        const userId = emailId.split('@')[0];
        setFieldErrors(errors);

        if (
            errors.firstName ||
            errors.lastName ||
            errors.emailId ||
            errors.userRole ||
            errors.category ||
            errors.orgName
        )
            return;

        const newUserData: Partial<UserInterface> = {
            firstName: firstName,
            lastName: lastName,
            emailId: emailId,
            userRole: userRole,
            userId,
            privilegedMode: isAdminRole(userRole) ? true : privilegedMode,
            category: category,
            orgName: orgName,
        };

        createUserMutation.mutate(newUserData);
    };

    const categoryDropDownProps = {
        categories: userCategories?.categories ?? [],
        category,
        setCategory,
        categoriesLoading: categoriesLoading,
        categoriesFetchingError: categoriesError as ApiError,
        fieldError: fieldErrors.category,
    };

    const handleSetUserRole = (value: string) => {
        if (
            userRole === UserRoles.ACCOUNT_EXECUTIVE &&
            value !== UserRoles.ACCOUNT_EXECUTIVE
        )
            setCategory(null);
        // Reset orgName if switching away from AE
        if (value !== UserRoles.ACCOUNT_EXECUTIVE) setOrgName(null);
        setUserRole(value);
    };

    const userRoleDropDownProps = {
        userRole,
        setUserRole: handleSetUserRole,
        error: fieldErrors.userRole,
    };

    return {
        inputFields,
        fieldErrors,
        onCancel,
        handleSubmit,
        userRoleDropDownProps,
        categoryDropDownProps,
        error: createUserMutation.error,
        privilegedMode,
        setPrivilegedMode,
        isError: createUserMutation.isError,
        formData: {
            firstName,
            lastName,
            emailId,
            userRole,
            category,
            privilegedMode,
            orgName,
        },
        isUserRoleAccountExecutive,
        orgName,
        setOrgName,
        deletedUserRestorePopover,
        setDeletedUserRestorePopover,
        handleRestoreDeletedUser,
    };
};
